import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import SpendAreaChart from "components/common/SpendAreaChart";
import { Card } from "@outpave/ds";
import type { TrendDirection } from "@outpave/ds";
import { formatCashFull } from "utils/formatCash";
import { SpendSummary } from "hooks/useByocDashboard";

export interface ExternalCardSpendChartProps {
  spend: SpendSummary;
  loading?: boolean;
  /** The spend query failed, so `spend` is the empty fallback rather than a
   *  measurement. Without this the chart reads a failure as zero spend. */
  error?: boolean;
}

/**
 * Company card spend across the trailing window.
 *
 * Domain wrapper only: the heading, the headline figure and the trend live
 * here, and the plot is `SpendAreaChart`. Same split as `PlatformBalanceChart`
 * over `SpendGraph`.
 *
 * The series is bucketed in the client because no server query returns daily
 * spend - `topSpending` is hardcoded to month buckets and `getCashFlow` returns
 * a weekly running balance, not spend.
 */
const ExternalCardSpendChart: React.FC<ExternalCardSpendChartProps> = ({
  spend,
  loading = false,
  error = false,
}) => {
  const { t } = useTranslation();

  const { changePercent } = spend.comparison;
  const hasComparison = !error && changePercent !== null;

  const direction: TrendDirection =
    !hasComparison || changePercent === 0
      ? "flat"
      : changePercent > 0
        ? "up"
        : "down";

  const points = useMemo(
    () => spend.daily.map((day) => ({ x: day.date, y: day.total })),
    [spend.daily],
  );

  return (
    <Card
      variant="outlined"
      title={t("External card spend")}
      value={
        loading
          ? ""
          : error
            ? "-"
            : formatCashFull(spend.comparison.current, spend.currency)
      }
      // While loading, the empty caption and chip keep their line, so the figure does not jump when they land.
      caption={
        loading
          ? ""
          : hasComparison
            ? t("Last {{n}} days", { n: spend.windowDays })
            : undefined
      }
      trend={
        loading
          ? { text: "" }
          : hasComparison
            ? {
                direction,
                sentiment: "neutral",
                text: t("{{n}}% vs prior {{days}} days", {
                  n: Math.abs(changePercent),
                  days: spend.windowDays,
                }),
              }
            : undefined
      }
      loading={loading}
      sx={{ height: "100%", minHeight: 0 }}
    >
      <SpendAreaChart
        data={error ? [] : points}
        currency={spend.currency}
        loading={loading}
        emptyMessage={error ? t("Something went wrong") : undefined}
      />
    </Card>
  );
};

export default ExternalCardSpendChart;
